/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search, perPage) => {
  let params = `include_contact_inboxes=true&page=${page}&sort=${sortAttr}`;
  if (perPage) {
    params = `${params}&per_page=${perPage}`;
  }
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '', perPage = null) {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      '',
      perPage
    )}`;
    return axios.get(requestURL);
  }

  show(id, { includeContactInboxes = false } = {}) {
    const includeFlag = includeContactInboxes ? 'true' : 'false';
    return axios.get(
      `${this.url}/${id}?include_contact_inboxes=${includeFlag}`
    );
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  assign(contactId, payload) {
    return axios.post(`${this.url}/${contactId}/assignments`, payload);
  }

  collaborators(contactId) {
    return axios.get(`${this.url}/${contactId}/collaborators`);
  }

  addCollaborator(contactId, userId, conversationId) {
    return axios.post(`${this.url}/${contactId}/collaborators`, {
      user_id: userId,
      conversation_id: conversationId,
    });
  }

  removeCollaborator(contactId, userId, conversationId) {
    return axios.delete(`${this.url}/${contactId}/collaborators/${userId}`, {
      params: { conversation_id: conversationId },
    });
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  getGoogleCalendarEvents(contactId, bucket = 'upcoming', page = 1) {
    return axios.get(`${this.url}/${contactId}/google_calendar_events`, {
      params: { bucket, page },
    });
  }

  initiateCall(contactId, inboxId) {
    return axios.post(`${this.url}/${contactId}/call`, {
      inbox_id: inboxId,
    });
  }

  updateContactLabels(contactId, labels, conversationId) {
    return axios.post(`${this.url}/${contactId}/labels`, {
      labels,
      ...(conversationId ? { conversation_id: conversationId } : {}),
    });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '', options = {}) {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search,
      options.perPage
    )}`;
    return axios.get(requestURL, { signal: options.signal });
  }

  active(page = 1, sortAttr = 'name', perPage = null) {
    let requestURL = `${this.url}/active?${buildContactParams(
      page,
      sortAttr,
      '',
      '',
      perPage
    )}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload, perPage) {
    let requestURL = `${this.url}/filter?${buildContactParams(
      page,
      sortAttr,
      '',
      '',
      perPage
    )}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  getImportStatus() {
    return axios.get(`${this.url}/import_status`);
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }
}

export default new ContactAPI();
